<template>
  <div class="relative min-h-[480px] hero-section overflow-hidden">
    <!-- Background hero image covering only the hero area -->
    <div class="absolute inset-0 h-full -z-10 pointer-events-none">
      <transition-group name="hero-fade" class="w-full h-full">
        <img :key="'current-' + slides[currentIndex].src" :src="slides[currentIndex].src"
          :alt="slides[currentIndex].alt" fetchpriority="high"
          class="w-full h-full object-cover object-center select-none absolute inset-0" />
        <img v-if="prevIndex !== null" :key="'prev-' + slides[prevIndex].src" :src="slides[prevIndex].src"
          :alt="slides[prevIndex].alt" loading="lazy" class="w-full h-full object-cover object-center select-none absolute inset-0" />
      </transition-group>
    </div>

    <div class="relative z-10 h-full flex items-center">
      <main class="mt-0 mx-auto w-full max-w-[1440px] px-6">
        <div class="grid grid-cols-1 lg:grid-cols-1 xl:grid-cols-2 gap-12 items-center h-full w-full">
          <!-- Left Content -->
          <div class="text-left text-white drop-shadow-md justify-self-start">
            <div class="relative animate-fadeIn">
              <h1
                class="text-4xl sm:text-5xl md:text-6xl font-black tracking-normal leading-[1.15] md:leading-[1.2] text-shadow-hero">
                <template v-if="abConfig.headline">
                  <span class="block">{{ abConfig.headline }}</span>
                </template>
                <template v-else>
                  <span class="block">Sistemas inteligentes</span>
                  <span class="block">para sua empresa</span>
                  <span class="block">crescer</span>
                </template>
              </h1>
            </div>

            <p
              class="mt-4 text-lg sm:text-xl text-white/90 leading-relaxed animate-fadeIn delay-200 text-shadow-hero-sm">
              <template v-if="abConfig.subheadline">
                <span class="md:block">{{ abConfig.subheadline }}</span>
              </template>
              <template v-else>
                <span class="md:block">Soluções completas em gestão empresarial. Simplifique</span>
                <span class="md:block">processos, aumente a produtividade e tome decisões</span>
                <span class="md:block">mais assertivas com a Kali Sistemas.</span>
              </template>
            </p>

            <div class="mt-8 flex flex-col sm:flex-row gap-4 animate-fadeIn delay-300">
              <button @click="openScheduleDemo"
                class="group relative inline-flex items-center justify-center px-8 py-3 text-lg font-medium rounded-xl text-[#004d6b] bg-white/90 hover:bg-white transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl pointer-events-auto">
                <span class="relative flex items-center">
                  {{ abConfig.cta_text || 'Agendar Demo' }}
                  <svg class="ml-2 -mr-1 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none"
                    stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6">
                    </path>
                  </svg>
                </span>
              </button>

              <RouterLink to="/solucao/kali-gestao"
                class="group inline-flex items-center justify-center px-8 py-3 text-lg font-medium rounded-xl text-[#006086] bg-white hover:bg-[#e6f0f4] border-2 border-[#cce0eb] hover:border-[#b3d1e0] transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg pointer-events-auto">
                <span class="relative flex items-center">
                  Nossas Soluções
                  <svg class="ml-2 -mr-1 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none"
                    stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3">
                    </path>
                  </svg>
                </span>
              </RouterLink>
            </div>
          </div>

          <!-- Decorative overlay elements could go here if needed -->
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.hero-section {
  width: 100%;
  height: 100vh;
  /* full viewport */
  height: 100dvh;
  /* melhora em navegadores com UI dinâmica */
}

@media (orientation: landscape) and (aspect-ratio: 16/10) {
  .hero-section {
    width: 100%;
    height: 100vh;
  }
}

@media (orientation: portrait) and (aspect-ratio: 10/16) {
  .hero-section {
    width: 100%;
    height: 100vh;
  }
}

.bg-grid-pattern {
  background-image:
    linear-gradient(to right, #e5e7eb 1px, transparent 1px),
    linear-gradient(to bottom, #e5e7eb 1px, transparent 1px);
  background-size: 24px 24px;
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes blob {

  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  25% {
    transform: translate(20px, -20px) scale(1.1);
  }

  50% {
    transform: translate(-20px, 20px) scale(0.9);
  }

  75% {
    transform: translate(20px, 20px) scale(1.1);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-blob {
  animation: blob 10s infinite;
}

/* Sombra de texto para melhorar contraste sobre imagens claras */
.text-shadow-hero {
  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.65),
    0 1px 2px rgba(0, 0, 0, 0.55);
}

.text-shadow-hero-sm {
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
}

.animation-delay-2000 {
  animation-delay: 2s;
}
</style>

<script setup>
defineOptions({ name: "HomeHeroSection" });
import { ref, onMounted, onUnmounted } from "vue";
import { RouterLink } from "vue-router";
import heroBg4 from "@/assets/hero-bg4.webp";
import heroBg2 from "@/assets/hero-bg2.webp";
import heroBg6 from "@/assets/hero-bg6.webp";
import heroBg5 from "@/assets/hero-bg5.webp";
import { useAbTest } from "@/composables/useAbTest";

const { config: abConfig, initAbTest } = useAbTest();

const slides = [
  { src: heroBg4, alt: "Hero 1" },
  { src: heroBg2, alt: "Hero 2" },
  { src: heroBg6, alt: "Hero 3" },
  { src: heroBg5, alt: "Hero 4" },
];

const currentIndex = ref(0);
const prevIndex = ref(null);
let autoplayId = null;

const nextSlide = () => {
  prevIndex.value = currentIndex.value;
  currentIndex.value = (currentIndex.value + 1) % slides.length;
};

const startAutoplay = () => {
  stopAutoplay();
  autoplayId = setInterval(nextSlide, 10000);
};

const stopAutoplay = () => {
  if (autoplayId) {
    clearInterval(autoplayId);
    autoplayId = null;
  }
};

onMounted(() => {
  startAutoplay();
  initAbTest();
});
onUnmounted(() => stopAutoplay());

const openScheduleDemo = () => {
  window.dispatchEvent(new CustomEvent("open-demo-modal"));
};
</script>

<style scoped>
/* Modal transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 200ms ease;
}

.modal-fade-enter-active .relative.bg-white,
.modal-fade-leave-active .relative.bg-white {
  transition: transform 200ms ease, opacity 200ms ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .relative.bg-white {
  transform: scale(0.95) translateY(-8px);
}

.hero-fade-enter-active,
.hero-fade-leave-active {
  transition: opacity 1000ms cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-fade-enter-from,
.hero-fade-leave-to {
  opacity: 0;
}

.hero-fade-enter-to,
.hero-fade-leave-from {
  opacity: 1;
}

/* garantir sobreposição adequada durante o crossfade */
.hero-fade-leave-active,
.hero-fade-enter-active {
  position: absolute;
  inset: 0;
}
</style>
